import React from "react";

interface DoubleClickWrapperProps<T extends React.ElementType> {
  component: T;
  onSingleClick: (e: React.MouseEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  delay?: number; // default to 250ms
}

/**
 * This wrapper component provides a robust solution for handling both single and double-click events without
 * them interfering with each other. It solves the common issue where a browser's double-click event is
 * preceded by a single-click event, which would cause both handlers to fire.
 *
 * It uses a `setTimeout` to debounce the click event, waiting a short period (default 250ms) to see if a second click
 * occurs.
 */
export class DoubleClickWrapper<T extends React.ElementType> extends React.Component<DoubleClickWrapperProps<T> & React.ComponentProps<T>> {

  private timerId: number | null = null;

  private clearTimer = () => {
    if (this.timerId) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  };

  private startTimer = (callback: () => void) => {
    this.timerId = window.setTimeout(callback, this.props.delay || 250);
  };

  private handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.clearTimer();
    this.startTimer(() => {
      this.props.onSingleClick(e);
    });
  };

  private handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.clearTimer();
    this.props.onDoubleClick(e);
  };

  componentWillUnmount() {
    this.clearTimer();
  }

  render() {
    const { component: Component, children, onSingleClick, onDoubleClick, ...rest } = this.props;
    return (
      <Component onClick={this.handleClick} onDoubleClick={this.handleDoubleClick} {...rest}>
        {children}
      </Component>
    );
  }
}